import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, PencilSimple, Plus, Trash } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { useSharedSections } from "@/lib/useSharedSections";
import { AdminModal, BTN_BLACK, BTN_WHITE, INPUT, errText } from "@/components/pageSettings/AdminModal";
import { BlockFields, cleanBlock } from "./BlockFields";
import { DesignPreview, sampleCtx } from "./designPreview";

const ICON_BTN = "border-2 border-black bg-white p-1 hover:bg-black hover:text-white disabled:opacity-30";

function BlockRow({ b, i, n, label, open, onOpen, onChange, onMove, onRemove }) {
  const tid = `ty-builder-block-${i}`;
  return (
    <div className={`mt-2 border-2 border-black ${b.enabled === false ? "bg-zinc-100" : "bg-white"}`} data-testid={tid}>
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        <span className="text-sm font-bold" data-testid={`${tid}-label`}>{label}</span>
        <span className="ml-auto" />
        <Switch checked={b.enabled !== false} onCheckedChange={(v) => onChange({ ...b, enabled: v })} data-testid={`${tid}-enabled`} />
        <button type="button" className={ICON_BTN} onClick={onOpen} aria-label="Edit" data-testid={`${tid}-edit`}><PencilSimple size={12} /></button>
        <button type="button" className={ICON_BTN} disabled={i === 0} onClick={() => onMove(-1)} aria-label="Move up" data-testid={`${tid}-up`}><ArrowUp size={12} /></button>
        <button type="button" className={ICON_BTN} disabled={i === n - 1} onClick={() => onMove(1)} aria-label="Move down" data-testid={`${tid}-down`}><ArrowDown size={12} /></button>
        <button type="button" className={ICON_BTN} disabled={n === 1} onClick={onRemove} aria-label="Remove" data-testid={`${tid}-remove`}><Trash size={12} /></button>
      </div>
      {open && <BlockFields b={b} onChange={onChange} testId={tid} />}
    </div>
  );
}

function AddBlock({ labels, onAdd }) {
  const [type, setType] = useState("");
  return (
    <div className="mt-3 flex items-end gap-2">
      <label className="label-mono flex-1">Add a section
        <select className={INPUT} value={type} onChange={(e) => setType(e.target.value)} data-testid="ty-builder-add-type">
          <option value="">Choose…</option>
          {Object.entries(labels).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </label>
      <button type="button" className={BTN_WHITE} disabled={!type} onClick={() => { onAdd(type); setType(""); }} data-testid="ty-builder-add-btn">
        <Plus size={11} weight="bold" /> Add
      </button>
    </div>
  );
}

export function DesignBuilder({ design, labels, onBack, onClose }) {
  const shared = useSharedSections();
  const [name, setName] = useState(design.name);
  const [blocks, setBlocks] = useState(design.blocks || []);
  const [open, setOpen] = useState(null);
  const [saving, setSaving] = useState(false);
  const upd = (i, nb) => setBlocks((bs) => bs.map((b, j) => (j === i ? nb : b)));
  const move = (i, d) => {
    setBlocks((bs) => { const n = [...bs]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; });
    setOpen(null);
  };
  const save = async () => {
    setSaving(true);
    try {
      if (design.template) {
        await api.put(`/admin/thankyou-templates/${design.template}`, { blocks: blocks.map(cleanBlock) });
      } else {
        await api.put(`/admin/thankyou-designs/${design.id}`, { name: name.trim(), blocks: blocks.map(cleanBlock) });
      }
      toast.success("Design saved");
      onBack();
    } catch (e) {
      toast.error(errText(e, "Couldn't save the design"));
    } finally {
      setSaving(false);
    }
  };
  const footer = (
    <>
      <button type="button" className={BTN_WHITE} onClick={onBack} data-testid="ty-builder-back">Back to designs</button>
      <button type="button" className={BTN_BLACK} onClick={save} disabled={saving || name.trim().length < 2 || !blocks.length} data-testid="ty-builder-save">
        {saving ? "Saving…" : "Save design"}
      </button>
    </>
  );
  return (
    <AdminModal eyebrow="Thank-you design" title={name || "Untitled design"} onClose={onClose} footer={footer} testId="ty-builder-modal" wide>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
        <div>
          {design.template ? (
            <p className="border-2 border-black bg-yellow-50 p-2 text-[11px]" data-testid="ty-builder-template-note">
              You&apos;re editing the ready-made &ldquo;{design.name}&rdquo; design. Every page using it shows your changes after you save.
            </p>
          ) : (
            <label className="label-mono block">Design name
              <input className={INPUT} maxLength={60} value={name} onChange={(e) => setName(e.target.value)} data-testid="ty-builder-name" />
            </label>
          )}
          <div className="label-mono mt-4">Sections, top to bottom</div>
          {blocks.map((b, i) => (
            <BlockRow key={`${b.type}-${i}`} b={b} i={i} n={blocks.length} label={labels[b.type] || b.type} open={open === i}
                      onOpen={() => setOpen(open === i ? null : i)} onChange={(nb) => upd(i, nb)}
                      onMove={(d) => move(i, d)} onRemove={() => { setBlocks((bs) => bs.filter((_, j) => j !== i)); setOpen(null); }} />
          ))}
          {blocks.length < 12 && <AddBlock labels={labels} onAdd={(type) => { setBlocks((bs) => [...bs, { type, enabled: true }]); setOpen(blocks.length); }} />}
        </div>
        <DesignPreview blocks={blocks} ctx={sampleCtx(shared)} />
      </div>
    </AdminModal>
  );
}
